import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Slider,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import SaveIcon from '@mui/icons-material/Save';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadString,
} from 'firebase/storage';
import { auth, db, functions, storage } from '../firebase';
import { useWorkspace } from '../context/WorkspaceContext';
import {
  DashboardModuleBody,
  DashboardModuleRoot,
  DashboardModuleToolbar,
} from '../components/layout/DashboardLayout';
import PhaserGameMount from '../components/game/PhaserGameMount';
import AssetQueueTable from '../components/game/AssetQueueTable';
import AssetGenerator from '../components/game/AssetGenerator';
import { useHistoryCodeSandboxes } from '../hooks/useHistoryCodeSandboxes';
import { codeSandboxDisplayStatus } from '../lib/codeSandboxReview';
import { deleteProjectGalleryAssetClient } from '../lib/syncProjectGalleryAsset';
import { evaluateSoleSemanticKeyHolder } from '../lib/playtestAssetUrlMap';

/**
 * Compact status chip props for Gallery cards mirrored from the Render Queue.
 * @param {string} status
 * @returns {{ label: string, color: 'default'|'info'|'warning'|'success'|'error' }}
 */
function statusChipPropsForGallery(status) {
  switch (status) {
    case 'draft':
      return { label: 'Draft', color: 'default' };
    case 'pending':
      return { label: 'Pending', color: 'default' };
    case 'generating':
      return { label: 'Generating', color: 'info' };
    case 'needs_review':
      return { label: 'Needs review', color: 'warning' };
    case 'approved':
      return { label: 'Approved', color: 'success' };
    case 'failed':
      return { label: 'Failed', color: 'error' };
    default:
      return { label: status || 'Asset', color: 'default' };
  }
}

const CONFIG_TABS = [
  { id: 'core', label: 'Core Config' },
  { id: 'assets', label: 'Asset Generator' },
  { id: 'queue', label: 'Render Queue' },
  { id: 'ledger', label: 'Project Ledger' },
  { id: 'playtest', label: 'Playtest Arena' },
];

const ASSET_TYPE_OPTIONS = [
  'Sprite Sheet (Animation Cycle)',
  'Static Sprite (Prop/Item)',
  'Background (Parallax Layer)',
  'Tileset (Environment)',
  'UI Element (Icon/Button)',
];

const DEFAULT_ASSET_TYPE = ASSET_TYPE_OPTIONS[0];

/**
 * Game-oriented aspect ratios mapped to SDXL-native pixel sizes.
 * (Stability SDXL accepts width/height; newer APIs use aspect_ratio strings.)
 */
const ASPECT_RATIO_OPTIONS = [
  {
    id: '1:1',
    label: '1:1 — Square (sprites / tiles)',
    width: 1024,
    height: 1024,
  },
  {
    id: '16:9',
    label: '16:9 — Widescreen (backgrounds)',
    width: 1344,
    height: 768,
  },
  {
    id: '9:16',
    label: '9:16 — Portrait (tall characters)',
    width: 768,
    height: 1344,
  },
  {
    id: '3:2',
    label: '3:2 — Landscape (props / scenes)',
    width: 1216,
    height: 832,
  },
  {
    id: '21:9',
    label: '21:9 — Ultrawide (parallax / banners)',
    width: 1536,
    height: 640,
  },
];

const DEFAULT_ASPECT_RATIO = ASPECT_RATIO_OPTIONS[0].id;

const ASPECT_RATIO_IDS = new Set(ASPECT_RATIO_OPTIONS.map((o) => o.id));

/** Phaser sprite-sheet frame defaults (endFrame is last 0-based index). */
const DEFAULT_SPRITE_CONFIG = {
  frameWidth: 128,
  frameHeight: 128,
  endFrame: 3,
};

/**
 * @param {string} value
 * @returns {string}
 */
function normalizeAspectRatioId(value) {
  const id = typeof value === 'string' ? value.trim() : '';
  return ASPECT_RATIO_IDS.has(id) ? id : DEFAULT_ASPECT_RATIO;
}

/**
 * @param {object} asset
 * @returns {boolean}
 */
function isSpriteSheetAsset(asset) {
  return String(asset?.type || '').toLowerCase().includes('sprite sheet');
}

/**
 * @param {object|null|undefined} config
 * @returns {{ frameWidth: number, frameHeight: number, endFrame: number }}
 */
function normalizeSpriteConfig(config) {
  const raw = config && typeof config === 'object' ? config : {};
  const frameWidth = Math.min(
    512,
    Math.max(16, Math.round(Number(raw.frameWidth) || DEFAULT_SPRITE_CONFIG.frameWidth)),
  );
  const frameHeight = Math.min(
    512,
    Math.max(16, Math.round(Number(raw.frameHeight) || DEFAULT_SPRITE_CONFIG.frameHeight)),
  );
  const endFrameRaw = Number(raw.endFrame);
  const endFrame = Math.min(
    23,
    Math.max(
      0,
      Number.isFinite(endFrameRaw) ? Math.round(endFrameRaw) : DEFAULT_SPRITE_CONFIG.endFrame,
    ),
  );
  return { frameWidth, frameHeight, endFrame };
}

const requestAiSprite = httpsCallable(functions, 'requestAiSprite', {
  // Creative upscale polls Stability for up to ~4 minutes.
  timeout: 320000,
});
const scaffoldGameRepo = httpsCallable(functions, 'scaffoldGameRepo');
const syncGameProjectDesignMd = httpsCallable(
  functions,
  'syncGameProjectDesignMd',
);

/** Seeded when companies/{id}/prompt_modifiers is empty. */
const DEFAULT_MODIFIER_TEMPLATES = [
  {
    id: 'sprite_sheet_default',
    name: 'Sprite Sheet Default',
    text:
      '(Mandatory strict format: 2D game sprite sheet, sequential animation frames in a perfect grid, uniform poses, flat solid green background for chroma key, full body visible, identical character design across all frames, pixel art style).',
  },
  {
    id: 'background_default',
    name: 'Background Default',
    text:
      '(Mandatory strict format: 2D seamless horizontally repeating game background, no UI elements, flat lighting).',
  },
];

/**
 * @param {*} value
 * @returns {string}
 */
function formatFirestoreTime(value) {
  if (!value) return '—';
  const d = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/**
 * @param {number} amount
 * @returns {string}
 */
function formatUsd(amount) {
  return `$${Number(amount || 0).toFixed(2)}`;
}

/**
 * Approved gallery assets eligible as character / image references.
 * Any approved asset with a usable imageUrl can be selected.
 * @param {object} asset
 * @returns {boolean}
 */
function isCharacterReferenceCandidate(asset) {
  const url =
    (typeof asset?.imageUrl === 'string' && asset.imageUrl.trim()) ||
    (typeof asset?.previewUrl === 'string' && asset.previewUrl.trim()) ||
    '';
  return url.length > 0;
}

/**
 * @param {File} file
 * @returns {Promise<string>} data URL
 */
function readImageFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error('Could not read image file.'));
    };
    reader.onerror = () => reject(reader.error || new Error('File read failed.'));
    reader.readAsDataURL(file);
  });
}

/**
 * Strip data-URL prefix for Stability init_image payloads.
 * @param {string} dataUrlOrBase64
 * @returns {string}
 */
function toRawBase64(dataUrlOrBase64) {
  const value = typeof dataUrlOrBase64 === 'string' ? dataUrlOrBase64.trim() : '';
  if (!value) return '';
  const comma = value.indexOf(',');
  if (value.startsWith('data:') && comma >= 0) return value.slice(comma + 1);
  return value;
}

/**
 * Best-effort Storage object path from a Firebase download URL.
 * @param {string} imageUrl
 * @returns {string}
 */
function storagePathFromDownloadUrl(imageUrl) {
  try {
    const parsed = new URL(String(imageUrl || ''));
    const match = parsed.pathname.match(/\/o\/(.+)$/);
    if (!match?.[1]) return '';
    return decodeURIComponent(match[1]);
  } catch {
    return '';
  }
}

function createAssetRow() {
  return {
    id: `asset_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    name: '',
    type: DEFAULT_ASSET_TYPE,
    imagePrompt: '',
    aspectRatio: DEFAULT_ASPECT_RATIO,
    actionType: 'generate',
    referenceAssetId: '',
    externalReferenceBase64: '',
    externalReferenceName: '',
  };
}

function createEmptyDraft() {
  return {
    title: '',
    genre: '',
    masterGameLogicPrompt: '',
    assets: [],
    aiSpendCapMaxCalls: '',
    aiSpendCapMaxUsd: '',
  };
}

function draftFromProject(project) {
  if (!project) return createEmptyDraft();
  const cap =
    project.aiSpendCap && typeof project.aiSpendCap === 'object'
      ? project.aiSpendCap
      : {};
  return {
    title: project.title || '',
    genre: project.genre || '',
    masterGameLogicPrompt: project.masterGameLogicPrompt || '',
        assets: Array.isArray(project.assets)
      ? project.assets.map((a) => ({
          id: a.id || createAssetRow().id,
          name: a.name || '',
          type: a.type || DEFAULT_ASSET_TYPE,
          imagePrompt: a.imagePrompt || '',
          aspectRatio: normalizeAspectRatioId(a.aspectRatio),
          actionType:
            a.actionType === 'upscale' ? 'upscale' : 'generate',
          referenceAssetId: a.referenceAssetId || '',
          externalReferenceBase64: '',
          externalReferenceName: '',
        }))
      : [],
    aiSpendCapMaxCalls:
      cap.maxCallCount != null && Number(cap.maxCallCount) > 0
        ? String(cap.maxCallCount)
        : '',
    aiSpendCapMaxUsd:
      cap.maxSpendUsd != null && Number(cap.maxSpendUsd) > 0
        ? String(cap.maxSpendUsd)
        : '',
  };
}

/**
 * AI Game Studio — project roster + configuration workspace.
 * @param {{ userData?: { companyId?: string } }} props
 */
export default function GameStudioHub({ userData }) {
  const { companyId } = useWorkspace();
  const resolvedCompanyId = companyId || userData?.companyId || null;

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState('');

  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [configTab, setConfigTab] = useState('core');
  const [draft, setDraft] = useState(() => createEmptyDraft());

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [designMdSyncError, setDesignMdSyncError] = useState('');
  const [generatingAssetId, setGeneratingAssetId] = useState(null);
  const [imageStrength, setImageStrength] = useState(0.55);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [previewAssetId, setPreviewAssetId] = useState(null);
  const [generateToast, setGenerateToast] = useState('');
  const [toastSeverity, setToastSeverity] = useState('info');
  const [approvingAsset, setApprovingAsset] = useState(false);
  const [approvedAssets, setApprovedAssets] = useState([]);
  const [projectAssets, setProjectAssets] = useState([]);
  const [approvedAssetsLoading, setApprovedAssetsLoading] = useState(false);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [scaffoldingRepo, setScaffoldingRepo] = useState(false);
  const [scaffoldError, setScaffoldError] = useState('');
  const [relinkRepoUrl, setRelinkRepoUrl] = useState('');
  const [relinkingRepo, setRelinkingRepo] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(false);
  const [deleteBatchError, setDeleteBatchError] = useState('');
  const [galleryBusyId, setGalleryBusyId] = useState(null);
  const [galleryDeleteTarget, setGalleryDeleteTarget] = useState(null);
  const [galleryDeleteSoleKey, setGalleryDeleteSoleKey] = useState('');

  const [modifierTemplates, setModifierTemplates] = useState([]);
  const [selectedModifierId, setSelectedModifierId] = useState('');
  const [isEditingModifier, setIsEditingModifier] = useState(false);
  const [modifierDraftText, setModifierDraftText] = useState('');
  const [savingModifier, setSavingModifier] = useState(false);

  const projectsRef = useMemo(() => {
    if (!resolvedCompanyId) return null;
    return collection(db, 'companies', resolvedCompanyId, 'game_projects');
  }, [resolvedCompanyId]);

  const selectedModifier = useMemo(
    () =>
      modifierTemplates.find((t) => t.id === selectedModifierId) ||
      modifierTemplates[0] ||
      null,
    [modifierTemplates, selectedModifierId],
  );

  useEffect(() => {
    if (!projectsRef) {
      setProjects([]);
      return undefined;
    }
    setProjectsLoading(true);
    setProjectsError('');
    const q = query(projectsRef, orderBy('updatedAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setProjects(rows);
        setProjectsLoading(false);
      },
      (err) => {
        console.error('game_projects snapshot:', err);
        setProjectsError(err.message || 'Could not load game projects.');
        setProjectsLoading(false);
      },
    );
    return () => unsubscribe();
  }, [projectsRef]);

  // Company-level prompt modifier templates (Asset Generator).
  useEffect(() => {
    if (!resolvedCompanyId) {
      setModifierTemplates([]);
      setSelectedModifierId('');
      setIsEditingModifier(false);
      setModifierDraftText('');
      return undefined;
    }

    const modifiersRef = collection(
      db,
      'companies',
      resolvedCompanyId,
      'prompt_modifiers',
    );
    let seedAttempted = false;

    const unsubscribe = onSnapshot(
      modifiersRef,
      async (snap) => {
        if (snap.empty && !seedAttempted) {
          seedAttempted = true;
          try {
            await Promise.all(
              DEFAULT_MODIFIER_TEMPLATES.map((template) =>
                setDoc(doc(modifiersRef, template.id), {
                  name: template.name,
                  text: template.text,
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                }),
              ),
            );
          } catch (err) {
            console.error('prompt_modifiers seed:', err);
            setModifierTemplates(DEFAULT_MODIFIER_TEMPLATES);
            setSelectedModifierId((prev) => prev || DEFAULT_MODIFIER_TEMPLATES[0].id);
          }
          return;
        }

        const rows = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            name: typeof data.name === 'string' ? data.name : d.id,
            text: typeof data.text === 'string' ? data.text : '',
          };
        });

        const nextRows = rows.length > 0 ? rows : DEFAULT_MODIFIER_TEMPLATES;
        setModifierTemplates(nextRows);
        setSelectedModifierId((prev) => {
          if (prev && nextRows.some((r) => r.id === prev)) return prev;
          return nextRows[0]?.id || '';
        });
      },
      (err) => {
        console.error('prompt_modifiers snapshot:', err);
        setModifierTemplates(DEFAULT_MODIFIER_TEMPLATES);
        setSelectedModifierId((prev) => prev || DEFAULT_MODIFIER_TEMPLATES[0].id);
      },
    );

    return () => unsubscribe();
  }, [resolvedCompanyId]);

  useEffect(() => {
    setSelectedProjectIds((prev) => {
      const validIds = new Set(projects.map((p) => p.id));
      const next = prev.filter((id) => validIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [projects]);

  useEffect(() => {
    if (!resolvedCompanyId || !selectedProjectId) {
      setApprovedAssets([]);
      setProjectAssets([]);
      setLedgerEntries([]);
      return undefined;
    }

    setApprovedAssetsLoading(true);
    setLedgerLoading(true);

    // No orderBy — avoids missing-index / missing-createdAt failures that
    // previously left the Character Reference dropdown empty.
    const assetsCol = collection(
      db,
      'companies',
      resolvedCompanyId,
      'game_projects',
      selectedProjectId,
      'assets',
    );
    const ledgerQuery = query(
      collection(
        db,
        'companies',
        resolvedCompanyId,
        'game_projects',
        selectedProjectId,
        'ledger',
      ),
      orderBy('timestamp', 'desc'),
    );

    const unsubAssets = onSnapshot(
      assetsCol,
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const aMs = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
            const bMs = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
            return bMs - aMs;
          });
        setApprovedAssets(rows);
        setProjectAssets(rows.filter(isCharacterReferenceCandidate));
        setApprovedAssetsLoading(false);
      },
      (err) => {
        console.error('game project assets:', err);
        setApprovedAssets([]);
        setProjectAssets([]);
        setApprovedAssetsLoading(false);
        setToastSeverity('error');
        setGenerateToast(err.message || 'Could not load project assets.');
      },
    );

    const unsubLedger = onSnapshot(
      ledgerQuery,
      (snap) => {
        setLedgerEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLedgerLoading(false);
      },
      (err) => {
        console.error('game project ledger:', err);
        setLedgerLoading(false);
      },
    );

    return () => {
      unsubAssets();
      unsubLedger();
    };
  }, [resolvedCompanyId, selectedProjectId]);

  const totalAiSpend = useMemo(
    () =>
      ledgerEntries.reduce((sum, row) => sum + Number(row.costUsd || 0), 0),
    [ledgerEntries],
  );

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId) || null,
    [projects, selectedProjectId],
  );

  const { patches: historySandboxPatches } = useHistoryCodeSandboxes(
    resolvedCompanyId,
  );

  /**
   * Approved / deployed sandbox file strings for Phaser playtest injection.
   * Prefers project-stored playtestFiles, then company sandbox history.
   */
  const approvedPlaytestFiles = useMemo(() => {
    const fromProject = Array.isArray(selectedProject?.playtestFiles)
      ? selectedProject.playtestFiles
      : Array.isArray(selectedProject?.approvedGameFiles)
        ? selectedProject.approvedGameFiles
        : [];

    const projectRows = fromProject
      .filter((row) => row && typeof row === 'object')
      .map((row) => ({
        id: row.id || row.path || row.target_file_path,
        path: row.path || row.target_file_path || row.file_path || '',
        content: row.content || row.patch_content || row.source || '',
      }))
      .filter((row) => String(row.content || '').trim());

    if (projectRows.length > 0) return projectRows;

    // Prefer project-scoped patches only. Patches without gameId are excluded
    // when a project is selected — they often belong to older games and crash
    // the Playtest Arena (e.g. platformer Player.js inside a Pong project).
    const allowed = new Set(['approved', 'success']);
    return (historySandboxPatches || [])
      .filter((patch) => {
        const display = codeSandboxDisplayStatus(patch);
        if (!allowed.has(display) && patch.status !== 'approved') return false;
        const path = String(
          patch.target_file_path || patch.file_path || '',
        ).replace(/\\/g, '/');
        if (!path) return false;
        const isGamePath =
          /\/(scenes|entities|config)\//i.test(path) ||
          /(^|\/)(scenes|entities|config)\//i.test(path) ||
          /Player\.js$/i.test(path) ||
          /gameConfig\.js$/i.test(path);
        if (!isGamePath) return false;
        if (selectedProjectId) {
          if (!patch.gameId || patch.gameId !== selectedProjectId) return false;
        }
        return Boolean(String(patch.patch_content || '').trim());
      })
      .map((patch) => ({
        id: patch.id,
        path: patch.target_file_path || patch.file_path || patch.id,
        content: patch.patch_content || '',
      }));
  }, [selectedProject, selectedProjectId, historySandboxPatches]);

  const workspaceActive = Boolean(selectedProjectId || isCreatingNew);

  const allProjectsSelected =
    projects.length > 0 && selectedProjectIds.length === projects.length;
  const someProjectsSelected =
    selectedProjectIds.length > 0 && !allProjectsSelected;

  const patchDraft = useCallback((partial) => {
    setDraft((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetWorkspace = useCallback(() => {
    setSelectedProjectId(null);
    setIsCreatingNew(false);
    setConfigTab('core');
    setDraft(createEmptyDraft());
    setSaveError('');
    setSaveSuccess('');
    setDesignMdSyncError('');
    setPreviewImageUrl(null);
    setPreviewAssetId(null);
    setScaffoldError('');
  }, []);

  const handleNewProject = () => {
    resetWorkspace();
    setIsCreatingNew(true);
  };

  const handleSelectProject = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    setSelectedProjectId(projectId);
    setIsCreatingNew(false);
    setConfigTab('core');
    setDraft(draftFromProject(project));
    setRelinkRepoUrl(project?.githubRepoUrl || '');
    setSaveError('');
    setSaveSuccess('');
    setDesignMdSyncError('');
    setPreviewImageUrl(null);
    setPreviewAssetId(null);
  };

  const handleToggleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedProjectIds(projects.map((p) => p.id));
    } else {
      setSelectedProjectIds([]);
    }
  };

  const handleToggleProjectSelection = (projectId) => {
    setSelectedProjectIds((prev) =>
      (prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]),
    );
  };

  const handleDeleteBatch = async () => {
    if (!resolvedCompanyId || selectedProjectIds.length === 0) return;

    setDeletingBatch(true);
    setDeleteBatchError('');
    try {
      const batch = writeBatch(db);
      selectedProjectIds.forEach((projectId) => {
        const projectRef = doc(
          db,
          'companies',
          resolvedCompanyId,
          'game_projects',
          projectId,
        );
        batch.delete(projectRef);
      });
      await batch.commit();

      const deletedActiveProject = selectedProjectIds.includes(selectedProjectId);
      setSelectedProjectIds([]);
      setDeleteDialogOpen(false);

      if (deletedActiveProject) {
        resetWorkspace();
      }
    } catch (e) {
      console.error('batch delete game projects:', e);
      setDeleteBatchError(e.message || 'Could not delete selected projects.');
    } finally {
      setDeletingBatch(false);
    }
  };

  const handleAddAsset = () => {
    patchDraft({ assets: [...draft.assets, createAssetRow()] });
  };

  const handleUpdateAsset = (assetId, partial) => {
    patchDraft({
      assets: draft.assets.map((a) => (a.id === assetId ? { ...a, ...partial } : a)),
    });
  };

  const handleSelectModifier = (modifierId) => {
    setSelectedModifierId(modifierId);
    setIsEditingModifier(false);
    setModifierDraftText('');
  };

  const handleStartEditModifier = () => {
    if (!selectedModifier) return;
    setModifierDraftText(selectedModifier.text || '');
    setIsEditingModifier(true);
  };

  const handleCancelEditModifier = () => {
    setIsEditingModifier(false);
    setModifierDraftText('');
  };

  const handleSaveModifier = async () => {
    if (!selectedModifierId) return;

    if (!resolvedCompanyId) {
      setToastSeverity('error');
      setGenerateToast('No company context — cannot save modifier.');
      return;
    }

    setSavingModifier(true);
    try {
      await updateDoc(
        doc(
          db,
          'companies',
          resolvedCompanyId,
          'prompt_modifiers',
          selectedModifierId,
        ),
        {
          text: modifierDraftText,
          updatedAt: serverTimestamp(),
          updatedByUid: auth.currentUser?.uid || null,
        },
      );
      setIsEditingModifier(false);
      setToastSeverity('success');
      setGenerateToast('Modifier template saved.');
    } catch (err) {
      console.error('save prompt_modifier:', err);
      setModifierTemplates((prev) =>
        prev.map((t) =>
          t.id === selectedModifierId ? { ...t, text: modifierDraftText } : t,
        ),
      );
      setIsEditingModifier(false);
      setToastSeverity('warning');
      setGenerateToast(
        err?.message
          ? `Saved locally only: ${err.message}`
          : 'Modifier saved locally (Firestore write failed).',
      );
    } finally {
      setSavingModifier(false);
    }
  };

  const handleRemoveAsset = (assetId) => {
    patchDraft({ assets: draft.assets.filter((a) => a.id !== assetId) });
    if (previewAssetId === assetId) {
      setPreviewImageUrl(null);
      setPreviewAssetId(null);
    }
  };

  const handleGenerateAsset = async (assetId) => {
    const asset = draft.assets.find((a) => a.id === assetId);
    if (!asset?.imagePrompt?.trim()) return;

    if (!selectedProjectId) {
      setToastSeverity('warning');
      setGenerateToast('Save the project first, then generate assets.');
      return;
    }

    const actionType =
      asset.actionType === 'upscale' ? 'upscale' : 'generate';
    const externalBase64 = toRawBase64(asset.externalReferenceBase64);
    const galleryRef = (projectAssets || []).find(
      (row) => row.id === asset.referenceAssetId,
    );
    const hasBaseImage = Boolean(externalBase64 || galleryRef?.imageUrl);

    if (actionType === 'upscale' && !hasBaseImage) {
      setToastSeverity('warning');
      setGenerateToast(
        'Upscale requires a base image — upload a file or pick an approved gallery asset.',
      );
      return;
    }

    setPreviewImageUrl(null);
    setPreviewAssetId(null);
    setGeneratingAssetId(assetId);

    try {
      const basePrompt = asset.imagePrompt.trim();
      const modifierText =
        actionType === 'upscale'
          ? ''
          : (selectedModifier?.text || '').trim();
      const payload = {
        actionType,
        prompt: basePrompt,
        type: asset.type || DEFAULT_ASSET_TYPE,
        gameId: selectedProjectId,
        systemModifiers: modifierText,
        modifierTemplateId:
          actionType === 'upscale' ? '' : selectedModifier?.id || '',
        aspect_ratio: normalizeAspectRatioId(asset.aspectRatio),
      };

      if (externalBase64) {
        payload.baseImage = externalBase64;
        payload.init_image = externalBase64;
        if (actionType === 'generate') {
          payload.strength = imageStrength;
          payload.image_strength = imageStrength;
        }
      } else if (galleryRef?.imageUrl) {
        payload.baseImageUrl = galleryRef.imageUrl;
        payload.referenceImageUrl = galleryRef.imageUrl;
        payload.referenceAssetId = galleryRef.id;
        if (actionType === 'generate') {
          payload.strength = imageStrength;
          payload.image_strength = imageStrength;
        }
      }

      const result = await requestAiSprite(payload);
      const base64Image = result?.data?.base64Image;
      if (!base64Image || typeof base64Image !== 'string') {
        throw new Error('The AI service returned no image data.');
      }
      setPreviewImageUrl(base64Image);
      setPreviewAssetId(assetId);
    } catch (e) {
      console.error('requestAiSprite:', e);
      setToastSeverity('error');
      const message =
        e?.details ||
        (typeof e?.message === 'string' && e.message !== 'INTERNAL'
          ? e.message
          : '') ||
        (actionType === 'upscale'
          ? 'Upscale failed. Check the base image and try again.'
          : 'Sprite generation failed. Check your prompt and try again.');
      setGenerateToast(String(message));
    } finally {
      setGeneratingAssetId(null);
    }
  };

  const handleDiscardPreview = () => {
    setPreviewImageUrl(null);
    setPreviewAssetId(null);
  };

  const handleApprovePreview = async () => {
    if (!previewImageUrl || !previewAssetId) return;
    if (!resolvedCompanyId || !selectedProjectId) {
      setToastSeverity('error');
      setGenerateToast('Save the project before approving assets.');
      return;
    }

    const asset = draft.assets.find((a) => a.id === previewAssetId);
    const assetName = asset?.name?.trim() || 'Untitled asset';
    const type = asset?.type || DEFAULT_ASSET_TYPE;
    const promptUsed = asset?.imagePrompt?.trim() || '';

    setApprovingAsset(true);
    try {
      const timestamp = Date.now();
      const storagePath = `companies/${resolvedCompanyId}/games/${selectedProjectId}/assets/${timestamp}.png`;
      const storageRef = ref(storage, storagePath);
      await uploadString(storageRef, previewImageUrl, 'data_url');
      const imageUrl = await getDownloadURL(storageRef);

      await addDoc(
        collection(
          db,
          'companies',
          resolvedCompanyId,
          'game_projects',
          selectedProjectId,
          'assets',
        ),
        {
          assetName,
          type,
          imageUrl,
          storagePath,
          promptUsed,
          ...(isSpriteSheetAsset({ type })
            ? { config: { ...DEFAULT_SPRITE_CONFIG } }
            : {}),
          createdAt: serverTimestamp(),
          createdByUid: auth.currentUser?.uid || null,
        },
      );

      setToastSeverity('success');
      setGenerateToast('Asset saved to project!');
      setPreviewImageUrl(null);
      setPreviewAssetId(null);
    } catch (e) {
      console.error('approve asset:', e);
      setToastSeverity('error');
      setGenerateToast(e.message || 'Could not save asset.');
    } finally {
      setApprovingAsset(false);
    }
  };

  const handleSaveGalleryAssetConfig = async (assetId, config) => {
    if (!assetId || !resolvedCompanyId || !selectedProjectId) return;

    const normalized = normalizeSpriteConfig(config);
    setGalleryBusyId(assetId);
    try {
      await updateDoc(
        doc(
          db,
          'companies',
          resolvedCompanyId,
          'game_projects',
          selectedProjectId,
          'assets',
          assetId,
        ),
        {
          config: normalized,
          updatedAt: serverTimestamp(),
          updatedByUid: auth.currentUser?.uid || null,
        },
      );
      setToastSeverity('success');
      setGenerateToast('Sprite sheet configuration saved.');
    } catch (e) {
      console.error('save gallery asset config:', e);
      setToastSeverity('error');
      setGenerateToast(e.message || 'Could not save sprite configuration.');
      throw e;
    } finally {
      setGalleryBusyId(null);
    }
  };

  const handleRenameGalleryAsset = async (assetId, nextName) => {
    const assetName = String(nextName || '').trim();
    if (!assetName) {
      setToastSeverity('warning');
      setGenerateToast('Asset name cannot be empty.');
      return;
    }
    if (!resolvedCompanyId || !selectedProjectId) return;

    setGalleryBusyId(assetId);
    try {
      await updateDoc(
        doc(
          db,
          'companies',
          resolvedCompanyId,
          'game_projects',
          selectedProjectId,
          'assets',
          assetId,
        ),
        {
          assetName,
          updatedAt: serverTimestamp(),
          updatedByUid: auth.currentUser?.uid || null,
        },
      );
      setToastSeverity('success');
      setGenerateToast('Asset name updated.');
    } catch (e) {
      console.error('rename gallery asset:', e);
      setToastSeverity('error');
      setGenerateToast(e.message || 'Could not rename asset.');
    } finally {
      setGalleryBusyId(null);
    }
  };

  const handleDeleteGalleryAsset = async (asset) => {
    if (!asset?.id || !resolvedCompanyId || !selectedProjectId) return;

    setGalleryBusyId(asset.id);
    try {
      const manifestId =
        typeof asset.manifestId === 'string' ? asset.manifestId.trim() : '';
      const manifestAssetId =
        typeof asset.manifestAssetId === 'string'
          ? asset.manifestAssetId.trim()
          : '';

      // Queue-linked gallery cards: remove from manifesto + Storage via helper.
      if (manifestId && manifestAssetId) {
        await deleteProjectGalleryAssetClient({
          companyId: resolvedCompanyId,
          projectId: selectedProjectId,
          manifestId,
          assetId: manifestAssetId,
          storagePath:
            typeof asset.storagePath === 'string' ? asset.storagePath : null,
        });

        try {
          const manifestRef = doc(
            db,
            'companies',
            resolvedCompanyId,
            'game_manifests',
            manifestId,
          );
          const snap = await getDoc(manifestRef);
          if (snap.exists()) {
            const data = snap.data() || {};
            const current = Array.isArray(data.assets) ? data.assets : [];
            const updatedAssets = current.filter((row, index) => {
              const id =
                typeof row?.id === 'string' && row.id.trim()
                  ? row.id.trim()
                  : `asset_${index + 1}`;
              return id !== manifestAssetId;
            });
            await updateDoc(manifestRef, {
              assets: updatedAssets,
              updatedAt: serverTimestamp(),
            });
          }
        } catch (manifestErr) {
          console.warn('gallery delete manifesto sync:', manifestErr);
        }
      } else {
        await deleteDoc(
          doc(
            db,
            'companies',
            resolvedCompanyId,
            'game_projects',
            selectedProjectId,
            'assets',
            asset.id,
          ),
        );

        try {
          const path =
            (typeof asset.storagePath === 'string' && asset.storagePath.trim()) ||
            storagePathFromDownloadUrl(asset.imageUrl);
          if (path) {
            await deleteObject(ref(storage, path));
          }
        } catch (storageErr) {
          console.warn('gallery asset storage cleanup:', storageErr);
        }
      }

      patchDraft({
        assets: draft.assets.map((row) =>
          row.referenceAssetId === asset.id
            ? { ...row, referenceAssetId: '' }
            : row,
        ),
      });

      setGalleryDeleteTarget(null);
      setGalleryDeleteSoleKey('');
      setToastSeverity('success');
      setGenerateToast('Asset removed from gallery.');
    } catch (e) {
      console.error('delete gallery asset:', e);
      setToastSeverity('error');
      setGenerateToast(e.message || 'Could not delete asset.');
    } finally {
      setGalleryBusyId(null);
    }
  };

  const handleSaveProject = async () => {
    setSaveError('');
    setSaveSuccess('');
    setDesignMdSyncError('');
    if (!resolvedCompanyId) {
      setSaveError('No workspace is linked to your account.');
      return;
    }
    if (!draft.title.trim()) {
      setSaveError('Enter a game title before saving.');
      return;
    }

    const callsRaw = (draft.aiSpendCapMaxCalls || '').trim();
    const spendRaw = (draft.aiSpendCapMaxUsd || '').trim();
    const callsN = callsRaw === '' ? null : Number(callsRaw);
    const spendN = spendRaw === '' ? null : Number(spendRaw);
    if (callsRaw !== '' && (!Number.isFinite(callsN) || callsN <= 0)) {
      setSaveError('AI spend max calls must be a positive number (or blank).');
      return;
    }
    if (spendRaw !== '' && (!Number.isFinite(spendN) || spendN <= 0)) {
      setSaveError('AI spend max USD must be a positive number (or blank).');
      return;
    }

    const payload = {
      title: draft.title.trim(),
      genre: draft.genre.trim(),
      masterGameLogicPrompt: draft.masterGameLogicPrompt || '',
      assets: draft.assets.map((a) => ({
        id: a.id,
        name: a.name.trim(),
        type: a.type,
        imagePrompt: a.imagePrompt || '',
        aspectRatio: normalizeAspectRatioId(a.aspectRatio),
        actionType: a.actionType === 'upscale' ? 'upscale' : 'generate',
        referenceAssetId: a.referenceAssetId || '',
      })),
      updatedAt: serverTimestamp(),
      updatedByUid: auth.currentUser?.uid || null,
    };

    if (callsN == null && spendN == null) {
      if (selectedProjectId) {
        payload.aiSpendCap = deleteField();
      }
    } else {
      payload.aiSpendCap = {
        ...(callsN != null ? { maxCallCount: Math.floor(callsN) } : {}),
        ...(spendN != null ? { maxSpendUsd: spendN } : {}),
      };
    }

    const hadLinkedRepo = Boolean(
      selectedProject?.githubOwner && selectedProject?.githubRepoName,
    );

    setSaving(true);
    try {
      let projectIdForSync = selectedProjectId;
      if (selectedProjectId) {
        await updateDoc(
          doc(db, 'companies', resolvedCompanyId, 'game_projects', selectedProjectId),
          payload,
        );
        setSaveSuccess(`Project "${payload.title}" saved.`);
      } else {
        const ref = await addDoc(projectsRef, {
          ...payload,
          createdAt: serverTimestamp(),
          createdByUid: auth.currentUser?.uid || null,
        });
        projectIdForSync = ref.id;
        setSelectedProjectId(ref.id);
        setIsCreatingNew(false);
        setSaveSuccess(`Project "${payload.title}" created.`);
      }

      // Non-blocking DESIGN.md mirror when a GitHub repo is linked.
      if (hadLinkedRepo && projectIdForSync) {
        try {
          await syncGameProjectDesignMd({ projectId: projectIdForSync });
        } catch (syncErr) {
          console.error('DESIGN.md sync after save failed:', syncErr);
          setDesignMdSyncError(
            syncErr?.message ||
              syncErr?.code ||
              'DESIGN.md sync failed.',
          );
        }
      }
    } catch (e) {
      console.error('save game project:', e);
      setSaveError(e.message || 'Could not save project.');
    } finally {
      setSaving(false);
    }
  };

  const handleScaffoldGithubRepo = async () => {
    setScaffoldError('');
    setDesignMdSyncError('');
    if (!selectedProjectId) {
      setScaffoldError('Save the project before initializing a repository.');
      return;
    }
    if (!draft.title.trim()) {
      setScaffoldError('Enter a game title before initializing a repository.');
      return;
    }
    if (selectedProject?.githubRepoUrl) {
      setScaffoldError('This project already has a GitHub repository.');
      return;
    }

    setScaffoldingRepo(true);
    try {
      const result = await scaffoldGameRepo({
        gameId: selectedProjectId,
        gameTitle: draft.title.trim(),
      });
      const data = result?.data || {};
      setSaveSuccess(
        data.html_url ?
          `GitHub repository created: ${data.html_url}` :
          'GitHub repository created.',
      );
      if (data.designMdSync && data.designMdSync.success === false) {
        setDesignMdSyncError(
          data.designMdSync.error ||
            'DESIGN.md sync failed after scaffold.',
        );
      }
    } catch (e) {
      console.error('scaffold github repo:', e);
      setScaffoldError(e.message || 'Could not initialize GitHub repository.');
    } finally {
      setScaffoldingRepo(false);
    }
  };

  /**
   * Clear broken GitHub metadata so Playtest is not blocked on a 404 repo.
   */
  const handleUnlinkGithubRepo = async () => {
    setScaffoldError('');
    if (!resolvedCompanyId || !selectedProjectId) return;
    setRelinkingRepo(true);
    try {
      await updateDoc(
        doc(db, 'companies', resolvedCompanyId, 'game_projects', selectedProjectId),
        {
          githubRepoUrl: deleteField(),
          githubCloneUrl: deleteField(),
          githubOwner: deleteField(),
          githubRepoName: deleteField(),
          githubDefaultBranch: deleteField(),
          updatedAt: serverTimestamp(),
          updatedByUid: auth.currentUser?.uid || null,
        },
      );
      setRelinkRepoUrl('');
      setSaveSuccess('GitHub repository unlinked from this project.');
    } catch (e) {
      console.error('unlink github repo:', e);
      setScaffoldError(e.message || 'Could not unlink GitHub repository.');
    } finally {
      setRelinkingRepo(false);
    }
  };

  /**
   * Point this project at an existing GitHub repo URL (owner/name).
   */
  const handleRelinkGithubRepo = async () => {
    setScaffoldError('');
    if (!resolvedCompanyId || !selectedProjectId) return;
    const raw = relinkRepoUrl.trim() || selectedProject?.githubRepoUrl || '';
    let owner = '';
    let repo = '';
    try {
      if (/^https?:\/\//i.test(raw)) {
        const u = new URL(raw);
        const parts = u.pathname.split('/').filter(Boolean);
        owner = parts[0] || '';
        repo = (parts[1] || '').replace(/\.git$/i, '');
      } else if (raw.includes('/')) {
        const parts = raw.replace(/^github\.com\//i, '').split('/').filter(Boolean);
        owner = parts[0] || '';
        repo = (parts[1] || '').replace(/\.git$/i, '');
      }
    } catch (err) {
      void err;
    }
    if (!owner || !repo) {
      setScaffoldError(
        'Enter a valid GitHub URL or owner/repo (e.g. tatotron/my-pong-repo).',
      );
      return;
    }

    setRelinkingRepo(true);
    try {
      const htmlUrl = `https://github.com/${owner}/${repo}`;
      await updateDoc(
        doc(db, 'companies', resolvedCompanyId, 'game_projects', selectedProjectId),
        {
          githubRepoUrl: htmlUrl,
          githubCloneUrl: `${htmlUrl}.git`,
          githubOwner: owner,
          githubRepoName: repo,
          githubDefaultBranch: 'main',
          updatedAt: serverTimestamp(),
          updatedByUid: auth.currentUser?.uid || null,
        },
      );
      setRelinkRepoUrl(htmlUrl);
      setSaveSuccess(`GitHub repository linked: ${htmlUrl}`);
      setDesignMdSyncError('');
      // Immediate DESIGN.md push when Master Prompt is already saved.
      if ((draft.masterGameLogicPrompt || '').trim()) {
        try {
          await syncGameProjectDesignMd({ projectId: selectedProjectId });
        } catch (syncErr) {
          console.error('DESIGN.md sync after relink failed:', syncErr);
          setDesignMdSyncError(
            syncErr?.message ||
              syncErr?.code ||
              'DESIGN.md sync failed.',
          );
        }
      }
    } catch (e) {
      console.error('relink github repo:', e);
      setScaffoldError(e.message || 'Could not relink GitHub repository.');
    } finally {
      setRelinkingRepo(false);
    }
  };

  const githubRepoDisplayUrl = useMemo(() => {
    const url = selectedProject?.githubRepoUrl || '';
    if (!url) return '';
    return url.replace(/^https?:\/\//, '');
  }, [selectedProject?.githubRepoUrl]);

  return (
    <DashboardModuleRoot sx={{ flexDirection: 'row', height: '100%', overflow: 'hidden' }}>
      {/* Left — Game Projects Roster (30%) */}
      <Box
        sx={{
          width: { xs: '100%', md: '30%' },
          minWidth: { md: 260 },
          maxWidth: { md: 360 },
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Game Projects Roster
          </Typography>

          {selectedProjectIds.length > 0 && (
            <Button
              variant="contained"
              color="error"
              fullWidth
              startIcon={
                deletingBatch ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
              onClick={() => {
                setDeleteBatchError('');
                setDeleteDialogOpen(true);
              }}
              disabled={deletingBatch}
              sx={{ mb: 1, fontWeight: 700 }}
            >
              Delete Selected ({selectedProjectIds.length})
            </Button>
          )}

          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox
              checked={allProjectsSelected}
              indeterminate={someProjectsSelected}
              onChange={handleToggleSelectAll}
              disabled={projects.length === 0 || projectsLoading}
              size="small"
              inputProps={{ 'aria-label': 'Select all game projects' }}
            />
            <Button
              variant="contained"
              fullWidth
              startIcon={<AddIcon />}
              onClick={handleNewProject}
              sx={{ fontWeight: 700 }}
            >
              New Game Project
            </Button>
          </Stack>
        </Box>

        <Dialog
          open={deleteDialogOpen}
          onClose={deletingBatch ? undefined : () => setDeleteDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete selected projects?</DialogTitle>
          <DialogContent>
            {deleteBatchError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {deleteBatchError}
              </Alert>
            )}
            <Typography variant="body2">
              Are you sure you want to permanently delete these{' '}
              {selectedProjectIds.length} selected game project
              {selectedProjectIds.length === 1 ? '' : 's'}? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deletingBatch}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteBatch}
              disabled={deletingBatch}
            >
              {deletingBatch ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(galleryDeleteTarget)}
          onClose={
            galleryBusyId
              ? undefined
              : () => {
                  setGalleryDeleteTarget(null);
                  setGalleryDeleteSoleKey('');
                }
          }
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Remove gallery asset?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Delete{' '}
              <strong>
                {galleryDeleteTarget?.assetName || 'this asset'}
              </strong>{' '}
              from the Asset Gallery? It will no longer be available as a
              character reference. This cannot be undone.
            </Typography>
            {galleryDeleteSoleKey ? (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                This is the only gallery asset that maps to Playtest texture
                key <strong>{galleryDeleteSoleKey}</strong>. This project has a
                linked GitHub game that loads that fixed key — deleting it may
                leave Playtest with missing sprites (green boxes). Prefer
                keeping an edited variant that still resolves to this key.
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => {
                setGalleryDeleteTarget(null);
                setGalleryDeleteSoleKey('');
              }}
              disabled={Boolean(galleryBusyId)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDeleteGalleryAsset(galleryDeleteTarget)}
              disabled={Boolean(galleryBusyId)}
            >
              {galleryBusyId === galleryDeleteTarget?.id
                ? 'Deleting…'
                : galleryDeleteSoleKey
                  ? 'Delete anyway'
                  : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <DashboardModuleBody sx={{ px: 0, py: 0 }}>
          {projectsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {projectsError && (
            <Alert severity="error" sx={{ m: 2 }}>
              {projectsError}
            </Alert>
          )}
          {!projectsLoading && !projectsError && projects.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No game projects yet. Click <strong>New Game Project</strong> to start.
            </Typography>
          )}
          <List dense disablePadding>
            {projects.map((project) => (
              <ListItemButton
                key={project.id}
                selected={selectedProjectId === project.id}
                onClick={() => handleSelectProject(project.id)}
                sx={{ px: 1.5, py: 1.25 }}
              >
                <Checkbox
                  edge="start"
                  checked={selectedProjectIds.includes(project.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleToggleProjectSelection(project.id);
                  }}
                  disabled={deletingBatch}
                  size="small"
                  sx={{ mr: 0.5 }}
                  inputProps={{ 'aria-label': `Select ${project.title || 'project'}` }}
                />
                <ListItemText
                  primary={project.title || 'Untitled project'}
                  secondary={project.genre ? `${project.genre}` : 'No genre set'}
                  primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
        </DashboardModuleBody>
      </Box>

      {/* Right — Configuration Workspace (70%) */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          bgcolor: 'background.default',
        }}
      >
        <DashboardModuleToolbar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {workspaceActive
                ? draft.title.trim() || (isCreatingNew ? 'New game project' : 'Configuration')
                : 'Configuration Workspace'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              Define game logic, sprite prompts, and playtest mounts
            </Typography>
          </Box>
          {workspaceActive && (
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveProject}
              disabled={saving}
              sx={{ flexShrink: 0, fontWeight: 700 }}
            >
              Save Project
            </Button>
          )}
        </DashboardModuleToolbar>

        {!workspaceActive ? (
          <DashboardModuleBody
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
            }}
          >
            <Box sx={{ textAlign: 'center', maxWidth: 420, color: 'text.secondary' }}>
              <SportsEsportsPlaceholder />
              <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mt: 2 }}>
                Select or create a game project
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Choose a project from the roster or start a new one to configure core logic,
                AI sprite prompts, and the playtest canvas.
              </Typography>
            </Box>
          </DashboardModuleBody>
        ) : (
          <>
            <Box
              sx={{
                flexShrink: 0,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                px: { xs: 1, sm: 2 },
              }}
            >
              <Tabs
                value={configTab}
                onChange={(_e, next) => setConfigTab(next)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Game studio configuration tabs"
              >
                {CONFIG_TABS.map((t) => (
                  <Tab key={t.id} value={t.id} label={t.label} sx={{ textTransform: 'none' }} />
                ))}
              </Tabs>
            </Box>

            <DashboardModuleBody sx={{ px: { xs: 2, sm: 3 }, py: 2, pb: 4 }}>
              {saveError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError('')}>
                  {saveError}
                </Alert>
              )}
              {saveSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess('')}>
                  {saveSuccess}
                </Alert>
              )}

              {configTab === 'core' && (
                <Stack spacing={2} sx={{ maxWidth: 760 }}>
                  <TextField
                    label="Game Title"
                    value={draft.title}
                    onChange={(e) => patchDraft({ title: e.target.value })}
                    fullWidth
                    placeholder="e.g. Neon Siege"
                  />
                  <TextField
                    label="Genre"
                    value={draft.genre}
                    onChange={(e) => patchDraft({ genre: e.target.value })}
                    fullWidth
                    placeholder="e.g. Platformer, Shooter, Puzzle"
                    helperText="Examples: Platformer, Shooter, Roguelike, Tower Defense"
                  />
                  <TextField
                    label="Master Game Logic Prompt"
                    value={draft.masterGameLogicPrompt}
                    onChange={(e) => patchDraft({ masterGameLogicPrompt: e.target.value })}
                    fullWidth
                    multiline
                    minRows={8}
                    placeholder="Describe the core mechanics, win/lose conditions, controls, and systems the AI should implement…"
                    helperText={
                      selectedProject?.githubOwner && selectedProject?.githubRepoName
                        ? 'Synced to repo-root DESIGN.md on save (Studio overwrites the file). Clearing this field deletes DESIGN.md from the repo.'
                        : 'These instructions guide the AI when building your HTML5 game code. Link a GitHub repo to sync them as DESIGN.md.'
                    }
                  />
                  {designMdSyncError ? (
                    <Alert
                      severity="warning"
                      sx={{ mt: 1 }}
                      onClose={() => setDesignMdSyncError('')}
                    >
                      DESIGN.md sync failed: {designMdSyncError}
                    </Alert>
                  ) : null}

                  <Divider />

                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <AccountTreeOutlinedIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle2" fontWeight={700}>
                        Source Control
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Scaffold a private GitHub repository so AI developer agents can commit
                      game code via pull requests. Configure GitHub in Workspace Settings first.
                    </Typography>

                    {scaffoldError && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setScaffoldError('')}>
                        {scaffoldError}
                      </Alert>
                    )}

                    {selectedProject?.githubRepoUrl ? (
                      <Stack spacing={1.5}>
                        <Alert
                          severity="success"
                          icon={<AccountTreeOutlinedIcon fontSize="inherit" />}
                          action={(
                            <IconButton
                              size="small"
                              color="inherit"
                              href={selectedProject.githubRepoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Open GitHub repository"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          )}
                        >
                          <Typography variant="body2" component="span">
                            🔗{' '}
                            <Link
                              href={selectedProject.githubRepoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ fontWeight: 600 }}
                            >
                              {githubRepoDisplayUrl || selectedProject.githubRepoUrl}
                            </Link>
                          </Typography>
                        </Alert>
                        <Typography variant="caption" color="text.secondary">
                          If Playtest Arena reports GitHub 404, the linked repo may be
                          renamed/deleted or the workspace PAT cannot see it. Relink to
                          the correct owner/repo or unlink to clear the broken pointer.
                        </Typography>
                        <TextField
                          size="small"
                          label="GitHub URL or owner/repo"
                          placeholder="tatotron/correct-pong-repo"
                          value={
                            relinkRepoUrl ||
                            selectedProject.githubRepoUrl ||
                            ''
                          }
                          onChange={(e) => setRelinkRepoUrl(e.target.value)}
                          fullWidth
                        />
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={relinkingRepo || scaffoldingRepo}
                            onClick={() => void handleRelinkGithubRepo()}
                          >
                            {relinkingRepo ? 'Saving…' : 'Relink repository'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={relinkingRepo || scaffoldingRepo}
                            onClick={() => void handleUnlinkGithubRepo()}
                          >
                            Unlink repository
                          </Button>
                        </Stack>
                      </Stack>
                    ) : (
                      <Stack spacing={1.5}>
                        <Button
                          variant="outlined"
                          startIcon={
                            scaffoldingRepo ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <AccountTreeOutlinedIcon />
                            )
                          }
                          onClick={handleScaffoldGithubRepo}
                          disabled={
                            scaffoldingRepo ||
                            relinkingRepo ||
                            !selectedProjectId ||
                            !draft.title.trim()
                          }
                        >
                          {scaffoldingRepo ? 'Initializing repository…' : 'Initialize GitHub Repository'}
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          Or link an existing repo:
                        </Typography>
                        <TextField
                          size="small"
                          label="GitHub URL or owner/repo"
                          placeholder="tatotron/my-existing-game"
                          value={relinkRepoUrl}
                          onChange={(e) => setRelinkRepoUrl(e.target.value)}
                          fullWidth
                        />
                        <Button
                          size="small"
                          variant="text"
                          disabled={
                            relinkingRepo ||
                            scaffoldingRepo ||
                            !selectedProjectId ||
                            !relinkRepoUrl.trim()
                          }
                          onClick={() => void handleRelinkGithubRepo()}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          Link existing repository
                        </Button>
                      </Stack>
                    )}

                    {!selectedProjectId && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Save the project first to enable repository scaffolding.
                      </Typography>
                    )}
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      AI spend budget (project default)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Soft defaults for tickets linked to this game. Tickets can
                      override per-ticket. Blank fields use the global fallback
                      (25 calls / $3.00) when no project cap is set.
                    </Typography>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                      <TextField
                        size="small"
                        label="Max AI calls"
                        value={draft.aiSpendCapMaxCalls || ''}
                        onChange={(e) =>
                          patchDraft({ aiSpendCapMaxCalls: e.target.value })
                        }
                        sx={{ width: 160 }}
                        inputProps={{ inputMode: 'numeric' }}
                        helperText="e.g. 25"
                      />
                      <TextField
                        size="small"
                        label="Max AI spend (USD)"
                        value={draft.aiSpendCapMaxUsd || ''}
                        onChange={(e) =>
                          patchDraft({ aiSpendCapMaxUsd: e.target.value })
                        }
                        sx={{ width: 180 }}
                        inputProps={{ inputMode: 'decimal' }}
                        helperText="e.g. 3.00"
                      />
                    </Stack>
                  </Box>
                </Stack>
              )}

              {configTab === 'assets' && (
                <Stack spacing={3}>
                  <AssetGenerator
                    selectedProject={selectedProject}
                    onNavigateToQueue={() => setConfigTab('queue')}
                    onSuccessToast={(message) => {
                      setToastSeverity('success');
                      setGenerateToast(message);
                    }}
                    onErrorToast={(message) => {
                      setToastSeverity('error');
                      setGenerateToast(message);
                    }}
                  />
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Manual sprite tools
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Optional one-off Stability generations with character references.
                      Prefer the manifest flow above for full game art packs.
                    </Typography>
                  </Box>
                  <AssetGeneratorSection
                    assets={draft.assets}
                    generatingAssetId={generatingAssetId}
                    previewImageUrl={previewImageUrl}
                    previewAssetId={previewAssetId}
                    approvingAsset={approvingAsset}
                    approvedAssets={approvedAssets}
                    projectAssets={projectAssets}
                    approvedAssetsLoading={approvedAssetsLoading}
                    projectSaved={Boolean(selectedProjectId)}
                    imageStrength={imageStrength}
                    onImageStrengthChange={setImageStrength}
                    modifierTemplates={modifierTemplates}
                    selectedModifierId={selectedModifier?.id || selectedModifierId}
                    selectedModifierText={selectedModifier?.text || ''}
                    isEditingModifier={isEditingModifier}
                    modifierDraftText={modifierDraftText}
                    savingModifier={savingModifier}
                    onSelectModifier={handleSelectModifier}
                    onStartEditModifier={handleStartEditModifier}
                    onCancelEditModifier={handleCancelEditModifier}
                    onModifierDraftChange={setModifierDraftText}
                    onSaveModifier={handleSaveModifier}
                    onAddAsset={handleAddAsset}
                    onUpdateAsset={handleUpdateAsset}
                    onRemoveAsset={handleRemoveAsset}
                    onGenerateAsset={handleGenerateAsset}
                    onDiscardPreview={handleDiscardPreview}
                    onApprovePreview={handleApprovePreview}
                    onRenameGalleryAsset={handleRenameGalleryAsset}
                    onRequestDeleteGalleryAsset={(asset) => {
                      const repoLinked = Boolean(
                        selectedProject?.githubRepoUrl ||
                          (selectedProject?.githubOwner &&
                            selectedProject?.githubRepoName),
                      );
                      let soleKey = '';
                      if (repoLinked && asset) {
                        const risk = evaluateSoleSemanticKeyHolder(
                          asset,
                          approvedAssets,
                        );
                        if (risk.sole && risk.semanticKey) {
                          soleKey = risk.semanticKey;
                        }
                      }
                      setGalleryDeleteSoleKey(soleKey);
                      setGalleryDeleteTarget(asset);
                    }}
                    onSaveGalleryAssetConfig={handleSaveGalleryAssetConfig}
                    galleryBusyId={galleryBusyId}
                  />
                </Stack>
              )}

              {configTab === 'queue' && (
                <Stack spacing={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    Manifest-driven render queue for AI-planned sprites, backgrounds, and UI.
                    Live from game_manifests for the active project.
                  </Typography>
                  <AssetQueueTable
                    companyId={resolvedCompanyId}
                    projectId={selectedProjectId}
                  />
                </Stack>
              )}

              {configTab === 'ledger' && (
                <ProjectLedgerSection
                  entries={ledgerEntries}
                  loading={ledgerLoading}
                  totalSpend={totalAiSpend}
                />
              )}

              {configTab === 'playtest' && (
                <Box sx={{ maxWidth: 960, width: '100%' }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Playtest Arena
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Phaser.js mounts here for in-browser playtesting. When a GitHub repo
                    is linked, engine files are pulled fresh from <code>main</code> with
                    cache-busting (not from stale sandbox history). Use{' '}
                    <strong>Force Refresh Arena</strong> to clear local cache and re-fetch.
                  </Typography>
                  <PhaserGameMount
                    height={420}
                    companyId={resolvedCompanyId}
                    selectedProject={selectedProject}
                    approvedFiles={approvedPlaytestFiles}
                    galleryAssets={projectAssets}
                  />
                  {approvedPlaytestFiles.length > 0 &&
                  !(
                    selectedProject?.githubRepoUrl ||
                    (selectedProject?.githubOwner && selectedProject?.githubRepoName)
                  ) ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Sandbox fallback: injecting {approvedPlaytestFiles.length} approved file
                      {approvedPlaytestFiles.length === 1 ? '' : 's'} (scaffold a GitHub repo
                      for live main-branch pulls).
                    </Typography>
                  ) : null}
                  {selectedProject?.lastBuildAt && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="caption" color="text.secondary">
                        Last build recorded for this project.
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </DashboardModuleBody>
          </>
        )}
      </Box>

      <Snackbar
        open={Boolean(generateToast)}
        autoHideDuration={5000}
        onClose={() => setGenerateToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toastSeverity}
          onClose={() => setGenerateToast('')}
          sx={{ width: '100%' }}
        >
          {generateToast}
        </Alert>
      </Snackbar>
    </DashboardModuleRoot>
  );
}

/**
 * Asset Generator tab — creatable type picker, character reference, AI generate.
 */
function AssetGeneratorSection({
  assets,
  generatingAssetId,
  previewImageUrl,
  previewAssetId,
  approvingAsset,
  approvedAssets,
  projectAssets,
  approvedAssetsLoading,
  projectSaved,
  imageStrength,
  onImageStrengthChange,
  modifierTemplates,
  selectedModifierId,
  selectedModifierText,
  isEditingModifier,
  modifierDraftText,
  savingModifier,
  onSelectModifier,
  onStartEditModifier,
  onCancelEditModifier,
  onModifierDraftChange,
  onSaveModifier,
  onAddAsset,
  onUpdateAsset,
  onRemoveAsset,
  onGenerateAsset,
  onDiscardPreview,
  onApprovePreview,
  onRenameGalleryAsset,
  onRequestDeleteGalleryAsset,
  onSaveGalleryAssetConfig,
  galleryBusyId,
}) {
  return (
    <Stack spacing={3} sx={{ maxWidth: 960 }}>
      <ModifierTemplateManager
        templates={modifierTemplates}
        selectedModifierId={selectedModifierId}
        selectedModifierText={selectedModifierText}
        isEditing={isEditingModifier}
        draftText={modifierDraftText}
        saving={savingModifier}
        onSelect={onSelectModifier}
        onStartEdit={onStartEditModifier}
        onCancelEdit={onCancelEditModifier}
        onDraftChange={onModifierDraftChange}
        onSave={onSaveModifier}
      />

      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Sprite &amp; asset requirements
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddAsset}
            sx={{ textTransform: 'none' }}
          >
            Add asset
          </Button>
        </Stack>
        {assets.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No assets defined yet. Add requirements for sprites the AI should generate.
          </Typography>
        )}
        <Stack spacing={2}>
          {assets.map((asset) => {
            const isGenerating = generatingAssetId === asset.id;
            const promptEmpty = !asset.imagePrompt?.trim();
            const showPreview =
              previewImageUrl && previewAssetId === asset.id;
            const actionType =
              asset.actionType === 'upscale' ? 'upscale' : 'generate';
            const isUpscale = actionType === 'upscale';
            const hasExternalRef = Boolean(asset.externalReferenceBase64);
            const hasGalleryRef = Boolean(asset.referenceAssetId);
            const hasBaseImage = hasExternalRef || hasGalleryRef;
            const usesImageToImage =
              !isUpscale && (hasExternalRef || hasGalleryRef);
            const generateDisabled =
              promptEmpty ||
              isGenerating ||
              !projectSaved ||
              (isUpscale && !hasBaseImage);

            return (
              <Box
                key={asset.id}
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                    ASSET REQUIREMENT
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => onRemoveAsset(asset.id)}
                    aria-label="Remove asset"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      label="Asset Name"
                      value={asset.name}
                      onChange={(e) => onUpdateAsset(asset.id, { name: e.target.value })}
                      fullWidth
                      placeholder="e.g. Player Idle"
                      size="small"
                    />
                    <Autocomplete
                      freeSolo
                      options={ASSET_TYPE_OPTIONS}
                      value={asset.type || ''}
                      onChange={(_e, newValue) =>
                        onUpdateAsset(asset.id, { type: newValue || '' })
                      }
                      onInputChange={(_e, newValue, reason) => {
                        if (reason === 'input') {
                          onUpdateAsset(asset.id, { type: newValue });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Type"
                          size="small"
                          placeholder={DEFAULT_ASSET_TYPE}
                          helperText="Pick a preset or type a custom asset class"
                        />
                      )}
                      sx={{ flex: 1, minWidth: { sm: 280 } }}
                    />
                  </Stack>
                  <FormControl fullWidth size="small">
                    <InputLabel id={`action-type-${asset.id}`}>
                      Mode
                    </InputLabel>
                    <Select
                      labelId={`action-type-${asset.id}`}
                      label="Mode"
                      value={actionType}
                      onChange={(e) =>
                        onUpdateAsset(asset.id, {
                          actionType:
                            e.target.value === 'upscale'
                              ? 'upscale'
                              : 'generate',
                        })
                      }
                    >
                      <MenuItem value="generate">
                        Generate New (Text-to-Image)
                      </MenuItem>
                      <MenuItem value="upscale">Upscale Existing</MenuItem>
                    </Select>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      {isUpscale
                        ? 'Uses Stability Creative Upscale on a base image from the gallery or an upload.'
                        : 'Standard Generation. Optional character reference enables image-to-image.'}
                    </Typography>
                  </FormControl>
                  {!isUpscale ? (
                    <FormControl fullWidth size="small">
                      <InputLabel id={`aspect-ratio-${asset.id}`}>
                        Aspect ratio
                      </InputLabel>
                      <Select
                        labelId={`aspect-ratio-${asset.id}`}
                        label="Aspect ratio"
                        value={normalizeAspectRatioId(asset.aspectRatio)}
                        onChange={(e) =>
                          onUpdateAsset(asset.id, {
                            aspectRatio: e.target.value,
                          })
                        }
                      >
                        {ASPECT_RATIO_OPTIONS.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: 'block' }}
                      >
                        Maps to Stability SDXL output size (e.g. 21:9 → 1536×640).
                        Image-to-image still follows the reference image size.
                      </Typography>
                    </FormControl>
                  ) : null}
                  <TextField
                    label={
                      isUpscale
                        ? 'Upscale Guidance Prompt'
                        : 'Image Generation Prompt'
                    }
                    value={asset.imagePrompt}
                    onChange={(e) =>
                      onUpdateAsset(asset.id, { imagePrompt: e.target.value })
                    }
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    placeholder={
                      isUpscale
                        ? 'Describe the subject to preserve while creatively upscaling'
                        : '16-bit pixel art of a soldier holding a rifle, transparent background'
                    }
                  />
                  <CharacterReferencePicker
                    asset={asset}
                    projectAssets={projectAssets}
                    loading={approvedAssetsLoading}
                    projectSaved={projectSaved}
                    onUpdateAsset={onUpdateAsset}
                    required={isUpscale}
                    mode={actionType}
                  />
                  {!isUpscale &&
                  (asset.referenceAssetId || asset.externalReferenceBase64) ? (
                    <Box sx={{ px: 0.5 }}>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        gutterBottom
                        display="block"
                      >
                        Reference Image Strength ({Number(imageStrength).toFixed(2)}) —
                        0.1 = Ignore, 1.0 = Strict Copy
                      </Typography>
                      <Slider
                        size="small"
                        min={0.1}
                        max={1.0}
                        step={0.05}
                        value={imageStrength}
                        onChange={(_e, value) => onImageStrengthChange?.(value)}
                        valueLabelDisplay="auto"
                        aria-label="Reference Image Strength"
                      />
                    </Box>
                  ) : null}
                  {!isUpscale ? (
                    <Typography variant="caption" color="text.secondary">
                      Active system modifier:{' '}
                      {modifierTemplates.find((t) => t.id === selectedModifierId)?.name ||
                        'None'}{' '}
                      (appended on generate)
                    </Typography>
                  ) : null}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      isGenerating ? (
                        <CircularProgress size={14} />
                      ) : (
                        <AutoAwesomeOutlinedIcon />
                      )
                    }
                    onClick={() => onGenerateAsset(asset.id)}
                    disabled={generateDisabled}
                    sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                  >
                    {isGenerating
                      ? isUpscale
                        ? 'Upscaling Asset...'
                        : 'Generating Asset...'
                      : isUpscale
                        ? 'Upscale Asset via AI'
                        : usesImageToImage
                          ? 'Generate via AI (Image-to-Image)'
                          : 'Generate Asset via AI'}
                  </Button>
                  {!projectSaved && (
                    <Typography variant="caption" color="warning.main" display="block">
                      Save the project before generating or approving assets.
                    </Typography>
                  )}
                  {isUpscale && projectSaved && !hasBaseImage ? (
                    <Typography variant="caption" color="warning.main" display="block">
                      Choose an approved gallery asset or upload a base image to upscale.
                    </Typography>
                  ) : null}
                  {showPreview && (
                    <AssetPreviewCard
                      imageUrl={previewImageUrl}
                      assetName={asset.name}
                      onDiscard={onDiscardPreview}
                      onApprove={onApprovePreview}
                      isApproving={approvingAsset}
                    />
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>

      <AssetGallery
        assets={approvedAssets}
        loading={approvedAssetsLoading}
        busyId={galleryBusyId}
        onRename={onRenameGalleryAsset}
        onRequestDelete={onRequestDeleteGalleryAsset}
        onSaveConfig={onSaveGalleryAssetConfig}
      />
    </Stack>
  );
}

/**
 * Editable company-level system modifier templates.
 */
function ModifierTemplateManager({
  templates,
  selectedModifierId,
  selectedModifierText,
  isEditing,
  draftText,
  saving,
  onSelect,
  onStartEdit,
  onCancelEdit,
  onDraftChange,
  onSave,
}) {
  const displayText = isEditing ? draftText : selectedModifierText;

  return (
    <Box
      sx={{
        p: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        System Modifiers (Auto-Appended)
      </Typography>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
            <InputLabel id="modifier-template-select-label">
              Modifier template
            </InputLabel>
            <Select
              labelId="modifier-template-select-label"
              label="Modifier template"
              value={selectedModifierId || ''}
              onChange={(e) => onSelect(e.target.value)}
              disabled={isEditing || templates.length === 0}
            >
              {templates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            size="small"
            onClick={onStartEdit}
            disabled={!selectedModifierId || isEditing}
            aria-label="Edit modifier template"
            sx={{ mt: 0.25 }}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>

        {isEditing ? (
          <TextField
            label="Modifier text"
            value={draftText}
            onChange={(e) => onDraftChange(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            size="small"
          />
        ) : (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'action.hover',
              border: 1,
              borderColor: 'divider',
              minHeight: 72,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: 'pre-wrap' }}
            >
              {displayText || 'No modifier text for this template.'}
            </Typography>
          </Box>
        )}

        {isEditing && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              startIcon={
                saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />
              }
              onClick={onSave}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              {saving ? 'Saving…' : 'Save Modifier'}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={onCancelEdit}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
          </Stack>
        )}

        <Typography variant="caption" color="text.secondary">
          The selected template is appended to every asset generation prompt.
        </Typography>
      </Stack>
    </Box>
  );
}

/**
 * Gallery dropdown + optional external image upload for img2img consistency.
 */
function CharacterReferencePicker({
  asset,
  projectAssets,
  loading,
  projectSaved,
  onUpdateAsset,
  required = false,
  mode = 'generate',
}) {
  const fileInputRef = useRef(null);
  const hasExternal = Boolean(asset.externalReferenceBase64);
  const candidates = projectAssets || [];
  const isUpscale = mode === 'upscale';
  const selectLabel = isUpscale
    ? required
      ? 'Base Image (Required)'
      : 'Base Image'
    : 'Character Reference (Optional)';
  const noneLabel = isUpscale
    ? 'Select an approved asset…'
    : 'None — Standard Text-to-Image';

  const handleSelectGalleryAsset = (value) => {
    onUpdateAsset(asset.id, {
      referenceAssetId: value,
      externalReferenceBase64: '',
      externalReferenceName: '',
    });
  };

  const handleExternalUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(file.type)) {
      return;
    }
    try {
      const dataUrl = await readImageFileAsDataUrl(file);
      onUpdateAsset(asset.id, {
        externalReferenceBase64: dataUrl,
        externalReferenceName: file.name,
        referenceAssetId: '',
      });
    } catch (err) {
      console.error('external reference upload:', err);
    }
  };

  const handleClearExternal = () => {
    onUpdateAsset(asset.id, {
      externalReferenceBase64: '',
      externalReferenceName: '',
    });
  };

  return (
    <Stack spacing={1}>
      <FormControl fullWidth size="small" disabled={hasExternal}>
        <InputLabel id={`char-ref-${asset.id}`}>{selectLabel}</InputLabel>
        <Select
          labelId={`char-ref-${asset.id}`}
          label={selectLabel}
          value={hasExternal ? '' : asset.referenceAssetId || ''}
          onChange={(e) => handleSelectGalleryAsset(e.target.value)}
          disabled={!projectSaved || loading || hasExternal}
          renderValue={(selected) => {
            if (!selected) return noneLabel;
            const match = candidates.find((row) => row.id === selected);
            if (!match) return selected;
            return match.assetName || match.name || match.id;
          }}
        >
          {!required ? (
            <MenuItem value="">
              <em>{noneLabel}</em>
            </MenuItem>
          ) : (
            <MenuItem value="">
              <em>{noneLabel}</em>
            </MenuItem>
          )}
          {candidates.map((refAsset) => (
            <MenuItem key={refAsset.id} value={refAsset.id}>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ py: 0.25 }}>
                <Avatar
                  src={refAsset.imageUrl}
                  alt={refAsset.assetName || refAsset.name || 'Asset'}
                  variant="rounded"
                  sx={{ width: 36, height: 36, bgcolor: 'action.hover' }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {refAsset.assetName || refAsset.name || refAsset.id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {refAsset.type || 'Approved asset'}
                  </Typography>
                </Box>
              </Stack>
            </MenuItem>
          ))}
        </Select>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: 'block' }}
        >
          {loading
            ? 'Loading project assets…'
            : candidates.length === 0
              ? isUpscale
                ? 'No approved gallery assets yet — upload a base image to upscale.'
                : 'No approved assets with images yet — upload an external reference or approve a sprite first.'
              : hasExternal
                ? 'External upload overrides the gallery selection.'
                : isUpscale
                  ? 'Creative Upscale uses this image as the source.'
                  : 'Uses image-to-image so the new pose keeps this character’s look.'}
        </Typography>
      </FormControl>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png, image/jpeg, image/webp"
        hidden
        onChange={handleExternalUpload}
      />

      {!hasExternal ? (
        <Button
          size="small"
          variant="text"
          startIcon={<UploadFileOutlinedIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={!projectSaved}
          sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        >
          {isUpscale ? 'Upload Base Image' : 'Upload External Reference'}
        </Button>
      ) : (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            p: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Box
            component="img"
            src={asset.externalReferenceBase64}
            alt={asset.externalReferenceName || 'External reference'}
            sx={{
              width: 48,
              height: 48,
              objectFit: 'contain',
              borderRadius: 0.5,
              bgcolor: '#e8e8e8',
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {isUpscale ? 'Base image' : 'External reference'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {asset.externalReferenceName || 'Uploaded image'}
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClearExternal}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            Clear
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

/**
 * Approved assets grid from Firestore subcollection.
 */
function AssetGallery({
  assets,
  loading,
  busyId,
  onRename,
  onRequestDelete,
  onSaveConfig,
}) {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [configureAsset, setConfigureAsset] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const startEdit = (asset) => {
    setEditingId(asset.id);
    setEditName(asset.assetName || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const saveEdit = async (assetId) => {
    await onRename?.(assetId, editName);
    cancelEdit();
  };

  /**
   * Force-download a Firebase Storage image via blob (cross-origin safe).
   * @param {React.MouseEvent} event
   * @param {{ id?: string, imageUrl?: string, assetName?: string, name?: string }} asset
   */
  const handleDownload = async (event, asset) => {
    event.stopPropagation();
    const imageUrl =
      typeof asset?.imageUrl === 'string' ? asset.imageUrl.trim() : '';
    if (!imageUrl || downloadingId) return;

    const assetId = asset?.id || imageUrl;
    setDownloadingId(assetId);
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const rawName =
        (typeof asset.assetName === 'string' && asset.assetName.trim()) ||
        (typeof asset.name === 'string' && asset.name.trim()) ||
        'asset';
      const safeName = rawName
        .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_')
        .replace(/\s+/g, '_')
        .slice(0, 80);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${safeName || 'asset'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        Asset Gallery
      </Typography>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {!loading && assets.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No assets yet. Generate a game manifesto or approve manual sprites to
          populate this gallery.
        </Typography>
      )}
      {!loading && assets.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 2,
          }}
        >
          {assets.map((asset) => {
            const isBusy = busyId === asset.id;
            const isEditing = editingId === asset.id;
            const isSheet = isSpriteSheetAsset(asset);
            const sheetConfig = isSheet ? normalizeSpriteConfig(asset.config) : null;
            const status =
              typeof asset.status === 'string' ? asset.status.trim() : '';
            const statusChip = status ? statusChipPropsForGallery(status) : null;
            const hasImage =
              typeof asset.imageUrl === 'string' && asset.imageUrl.trim();

            return (
              <Paper
                key={asset.id}
                variant="outlined"
                sx={{
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                  position: 'relative',
                }}
              >
                <Box
                  onClick={() => {
                    if (isSheet && !isBusy && hasImage) setConfigureAsset(asset);
                  }}
                  sx={{
                    aspectRatio: '1',
                    bgcolor: '#e8e8e8',
                    backgroundImage: `
                      linear-gradient(45deg, #d0d0d0 25%, transparent 25%),
                      linear-gradient(-45deg, #d0d0d0 25%, transparent 25%),
                      linear-gradient(45deg, transparent 75%, #d0d0d0 75%),
                      linear-gradient(-45deg, transparent 75%, #d0d0d0 75%)
                    `,
                    backgroundSize: '10px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isSheet && hasImage ? 'pointer' : 'default',
                    position: 'relative',
                  }}
                >
                  {hasImage ? (
                    <Box
                      component="img"
                      src={asset.imageUrl}
                      alt={asset.assetName || 'Approved asset'}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        pointerEvents: 'none',
                      }}
                    />
                  ) : (
                    <Stack spacing={0.5} alignItems="center" sx={{ px: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        textAlign="center"
                      >
                        {status === 'draft'
                          ? 'Awaiting generation'
                          : status === 'pending' || status === 'generating'
                            ? 'Generating…'
                            : status === 'failed'
                              ? 'Generation failed'
                              : 'No image yet'}
                      </Typography>
                    </Stack>
                  )}
                  {statusChip ? (
                    <Chip
                      size="small"
                      label={statusChip.label}
                      color={statusChip.color}
                      variant={
                        statusChip.color === 'default' ? 'outlined' : 'filled'
                      }
                      sx={{
                        position: 'absolute',
                        top: 6,
                        left: 6,
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        bgcolor:
                          statusChip.color === 'default'
                            ? 'rgba(255,255,255,0.9)'
                            : undefined,
                      }}
                    />
                  ) : null}
                  {isSheet && hasImage && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 6,
                        bottom: 6,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 0.5,
                        bgcolor: 'rgba(0,0,0,0.65)',
                        color: 'common.white',
                      }}
                    >
                      <Typography variant="caption" fontWeight={600}>
                        Configure sheet
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ p: 1.25 }}>
                  {isEditing ? (
                    <Stack spacing={1}>
                      <TextField
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        size="small"
                        fullWidth
                        autoFocus
                        label="Asset name"
                        disabled={isBusy}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            saveEdit(asset.id);
                          }
                          if (e.key === 'Escape') cancelEdit();
                        }}
                      />
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => saveEdit(asset.id)}
                          disabled={isBusy || !editName.trim()}
                          sx={{ textTransform: 'none', flex: 1 }}
                        >
                          {isBusy ? 'Saving…' : 'Save'}
                        </Button>
                        <Button
                          size="small"
                          onClick={cancelEdit}
                          disabled={isBusy}
                          sx={{ textTransform: 'none' }}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      spacing={0.5}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {asset.assetName || 'Untitled'}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          display="block"
                        >
                          {asset.type || '—'}
                          {asset.category ? ` · ${asset.category}` : ''}
                          {sheetConfig
                            ? ` · ${sheetConfig.frameWidth}×${sheetConfig.frameHeight}`
                            : ''}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0}>
                        {isSheet && hasImage && (
                          <IconButton
                            size="small"
                            aria-label="Configure sprite sheet"
                            onClick={() => setConfigureAsset(asset)}
                            disabled={isBusy}
                          >
                            <GridOnOutlinedIcon fontSize="inherit" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          aria-label="Download asset"
                          onClick={(e) => handleDownload(e, asset)}
                          disabled={
                            isBusy ||
                            !hasImage ||
                            downloadingId === asset.id
                          }
                        >
                          {downloadingId === asset.id ? (
                            <CircularProgress size={14} />
                          ) : (
                            <DownloadOutlinedIcon fontSize="inherit" />
                          )}
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Rename asset"
                          onClick={() => startEdit(asset)}
                          disabled={isBusy}
                        >
                          <EditOutlinedIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Delete asset"
                          onClick={() => onRequestDelete?.(asset)}
                          disabled={isBusy}
                          color="error"
                        >
                          {isBusy ? (
                            <CircularProgress size={14} />
                          ) : (
                            <DeleteOutlineIcon fontSize="inherit" />
                          )}
                        </IconButton>
                      </Stack>
                    </Stack>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      <AssetConfiguratorModal
        asset={configureAsset}
        open={Boolean(configureAsset)}
        saving={Boolean(configureAsset && busyId === configureAsset.id)}
        onClose={() => setConfigureAsset(null)}
        onSave={async (config) => {
          if (!configureAsset?.id) return;
          await onSaveConfig?.(configureAsset.id, config);
          setConfigureAsset(null);
        }}
      />
    </Box>
  );
}

/**
 * Visual Phaser frame slicer for approved sprite sheets.
 */
function AssetConfiguratorModal({ asset, open, saving, onClose, onSave }) {
  const initial = normalizeSpriteConfig(asset?.config);
  const [frameWidth, setFrameWidth] = useState(initial.frameWidth);
  const [frameHeight, setFrameHeight] = useState(initial.frameHeight);
  const [totalFrames, setTotalFrames] = useState(initial.endFrame + 1);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [previewFrame, setPreviewFrame] = useState(0);

  useEffect(() => {
    if (!open || !asset) return undefined;
    const next = normalizeSpriteConfig(asset.config);
    setFrameWidth(next.frameWidth);
    setFrameHeight(next.frameHeight);
    setTotalFrames(next.endFrame + 1);
    setNaturalSize({ width: 0, height: 0 });
    setPreviewFrame(0);
    return undefined;
  }, [open, asset]);

  const columns = useMemo(() => {
    if (!naturalSize.width || !frameWidth) return 1;
    return Math.max(1, Math.floor(naturalSize.width / frameWidth));
  }, [naturalSize.width, frameWidth]);

  const frameCells = useMemo(() => {
    const cells = [];
    for (let i = 0; i < totalFrames; i += 1) {
      const col = i % columns;
      const row = Math.floor(i / columns);
      cells.push({
        index: i,
        x: col * frameWidth,
        y: row * frameHeight,
      });
    }
    return cells;
  }, [totalFrames, columns, frameWidth, frameHeight]);

  const fitsSingleRow =
    !naturalSize.width || totalFrames * frameWidth <= naturalSize.width;

  useEffect(() => {
    if (!open || fitsSingleRow) return undefined;
    const id = window.setInterval(() => {
      setPreviewFrame((prev) => (prev + 1) % Math.max(1, totalFrames));
    }, 140);
    return () => window.clearInterval(id);
  }, [open, fitsSingleRow, totalFrames]);

  const previewCol = previewFrame % columns;
  const previewRow = Math.floor(previewFrame / columns);

  const handleSave = async () => {
    await onSave?.(
      normalizeSpriteConfig({
        frameWidth,
        frameHeight,
        endFrame: Math.max(0, totalFrames - 1),
      }),
    );
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Configure sprite sheet
        {asset?.assetName ? ` — ${asset.assetName}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            Align the grid to your sheet so Phaser can slice frames with{' '}
            <code>frameWidth</code>, <code>frameHeight</code>, and{' '}
            <code>endFrame</code> (last frame index).
          </Typography>

          <Box
            sx={{
              position: 'relative',
              display: 'inline-block',
              maxWidth: '100%',
              alignSelf: 'center',
              bgcolor: '#111',
              borderRadius: 1,
              overflow: 'hidden',
              lineHeight: 0,
            }}
          >
            {asset?.imageUrl ? (
              <>
                <Box
                  component="img"
                  src={asset.imageUrl}
                  alt={asset.assetName || 'Sprite sheet'}
                  onLoad={(e) => {
                    setNaturalSize({
                      width: e.currentTarget.naturalWidth || 0,
                      height: e.currentTarget.naturalHeight || 0,
                    });
                  }}
                  sx={{
                    display: 'block',
                    maxWidth: '100%',
                    height: 'auto',
                    imageRendering: 'pixelated',
                  }}
                />
                {naturalSize.width > 0 && naturalSize.height > 0 && (
                  <Box
                    component="svg"
                    viewBox={`0 0 ${naturalSize.width} ${naturalSize.height}`}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'none',
                    }}
                  >
                    {frameCells.map((cell) => (
                      <rect
                        key={cell.index}
                        x={cell.x}
                        y={cell.y}
                        width={frameWidth}
                        height={frameHeight}
                        fill="rgba(0, 255, 120, 0.08)"
                        stroke={cell.index === 0 ? '#00e676' : 'rgba(0, 230, 118, 0.85)'}
                        strokeWidth={cell.index === 0 ? 3 : 2}
                      />
                    ))}
                  </Box>
                )}
              </>
            ) : (
              <Typography variant="body2" color="grey.400" sx={{ p: 3 }}>
                No image available.
              </Typography>
            )}
          </Box>

          <Stack spacing={2} sx={{ px: 0.5 }}>
            <Box>
              <Typography variant="caption" fontWeight={700} gutterBottom display="block">
                Frame Width: {frameWidth}px
              </Typography>
              <Slider
                value={frameWidth}
                min={16}
                max={512}
                step={8}
                onChange={(_e, value) => setFrameWidth(value)}
                valueLabelDisplay="auto"
              />
            </Box>
            <Box>
              <Typography variant="caption" fontWeight={700} gutterBottom display="block">
                Frame Height: {frameHeight}px
              </Typography>
              <Slider
                value={frameHeight}
                min={16}
                max={512}
                step={8}
                onChange={(_e, value) => setFrameHeight(value)}
                valueLabelDisplay="auto"
              />
            </Box>
            <Box>
              <Typography variant="caption" fontWeight={700} gutterBottom display="block">
                Total Frames: {totalFrames} (endFrame = {Math.max(0, totalFrames - 1)})
              </Typography>
              <Slider
                value={totalFrames}
                min={1}
                max={24}
                step={1}
                onChange={(_e, value) => setTotalFrames(value)}
                valueLabelDisplay="auto"
                marks
              />
            </Box>
          </Stack>

          <Divider />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <Box>
              <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 1 }}>
                Animation Preview
              </Typography>
              {fitsSingleRow ? (
                <Box
                  key={`${frameWidth}-${frameHeight}-${totalFrames}-${asset?.imageUrl || ''}`}
                  sx={{
                    width: frameWidth,
                    height: frameHeight,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: '#1a1a1a',
                    imageRendering: 'pixelated',
                    backgroundImage: asset?.imageUrl ? `url(${asset.imageUrl})` : 'none',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: '0 0',
                    backgroundSize: naturalSize.width
                      ? `${naturalSize.width}px ${naturalSize.height}px`
                      : 'auto',
                    animation:
                      totalFrames > 1
                        ? `gameStudioSpritePreview ${Math.max(0.4, totalFrames * 0.14)}s steps(${totalFrames}) infinite`
                        : 'none',
                    '@keyframes gameStudioSpritePreview': {
                      from: { backgroundPosition: '0 0' },
                      to: {
                        backgroundPosition: `-${totalFrames * frameWidth}px 0`,
                      },
                    },
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: frameWidth,
                    height: frameHeight,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: '#1a1a1a',
                    imageRendering: 'pixelated',
                    backgroundImage: asset?.imageUrl ? `url(${asset.imageUrl})` : 'none',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: naturalSize.width
                      ? `${naturalSize.width}px ${naturalSize.height}px`
                      : 'auto',
                    backgroundPosition: `-${previewCol * frameWidth}px -${previewRow * frameHeight}px`,
                  }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 360 }}>
              {fitsSingleRow
                ? 'Preview uses CSS background-position stepping for a single-row strip.'
                : 'Sheet spans multiple rows — preview steps frames with JS using the same grid math Phaser will use.'}
              {naturalSize.width
                ? ` Image ${naturalSize.width}×${naturalSize.height}px · ${columns} col${columns === 1 ? '' : 's'}.`
                : ''}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={
            saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />
          }
          onClick={handleSave}
          disabled={saving || !asset?.imageUrl}
          sx={{ textTransform: 'none' }}
        >
          {saving ? 'Saving…' : 'Save Configuration'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Read-only AI spend ledger for the active game project.
 */
function ProjectLedgerSection({ entries, loading, totalSpend }) {
  return (
    <Stack spacing={2} sx={{ maxWidth: 960 }}>
      <Card variant="outlined">
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            TOTAL AI SPEND
          </Typography>
          <Typography variant="h4" fontWeight={800} color="primary.main">
            {formatUsd(totalSpend)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cumulative Stability AI generation costs for this game project.
          </Typography>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Timestamp</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Cost
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      No ledger entries yet. AI sprite generations will appear here.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {entries.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{formatFirestoreTime(row.timestamp)}</TableCell>
                  <TableCell>{row.action || '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {row.userUid ? `${String(row.userUid).slice(0, 8)}…` : '—'}
                  </TableCell>
                  <TableCell align="right">{formatUsd(row.costUsd)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}

/**
 * Staged AI asset preview — human approval before save.
 */
function AssetPreviewCard({ imageUrl, assetName, onDiscard, onApprove, isApproving }) {
  return (
    <Box
      sx={{
        mt: 0.5,
        p: 2,
        borderRadius: 1.5,
        border: 1,
        borderColor: 'divider',
        bgcolor: (t) =>
          t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50',
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        Asset Preview
        {assetName?.trim() ? ` — ${assetName.trim()}` : ''}
      </Typography>

      <Box
        sx={{
          width: 256,
          height: 256,
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          overflow: 'hidden',
          bgcolor: '#e8e8e8',
          backgroundImage: `
            linear-gradient(45deg, #d0d0d0 25%, transparent 25%),
            linear-gradient(-45deg, #d0d0d0 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #d0d0d0 75%),
            linear-gradient(-45deg, transparent 75%, #d0d0d0 75%)
          `,
          backgroundSize: '12px 12px',
          backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={assetName?.trim() ? `Preview of ${assetName}` : 'Generated asset preview'}
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>

      <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onDiscard}
          disabled={isApproving}
          sx={{ textTransform: 'none', borderColor: 'divider' }}
        >
          Discard
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={onApprove}
          disabled={isApproving}
          startIcon={isApproving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {isApproving ? 'Saving…' : 'Approve & Save'}
        </Button>
      </Stack>
    </Box>
  );
}

function SportsEsportsPlaceholder() {
  return (
    <Box
      sx={{
        width: 72,
        height: 72,
        mx: 'auto',
        borderRadius: 2,
        bgcolor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'text.secondary',
      }}
      aria-hidden
    >
      <SportsEsportsIcon sx={{ fontSize: 40 }} />
    </Box>
  );
}
